import React, {useEffect, useMemo, useState} from 'react';
import corporationService from "../../../../../services/web/corporation-service.js";
import {useQuery} from "@tanstack/react-query";
import {
    AdvancedMarker, APIProvider, InfoWindow,
    ControlPosition,
    MapControl,
    useAdvancedMarkerRef,
    useMap,
    Map as GoogleMap,
} from "@vis.gl/react-google-maps";
import projectUtils from "../../../../../utils/project-utils.js";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {DEFAULT_MAP_ID, normalizeCoordinate} from "../resources/map-utils.js";

const INDIVIDUAL_MAP_TYPES = {
    address_comercial: {
        label: "Endereços",
        singular: "Endereço",
        icon: "sw.comercial.svg",
    },
    address_residencial: {
        label: "Endereços",
        singular: "Endereço",
        icon: "sw.residencial.svg",
    },
    encounter: {
        label: "Abordagens",
        singular: "Abordagem",
        icon: "sw.abordagem.svg"
    },
    bo: {
        label: "BO",
        singular: "BO",
        icon: "sw.bogcm.svg"
    },
    occurrence: {
        label: "Ocorrências",
        singular: "Ocorrência",
        icon: "sw.ocorrencia.svg"
    },
    sos: {
        label: "SOS",
        singular: "SOS",
        icon: "sw.sos.svg"
    }
};

const toArray = (value) => Array.isArray(value) ? value : [];

const getPositionFromItem = (item) => {
    const lat = normalizeCoordinate(
        item?.lat ??
        item?.latitude ??
        item?.Latitude ??
        item?.LATITUDE ??
        item?.location?.lat ??
        item?.position?.lat
    );

    const lng = normalizeCoordinate(
        item?.lng ??
        item?.lon ??
        item?.long ??
        item?.longitude ??
        item?.Longitude ??
        item?.LONGITUDE ??
        item?.location?.lng ??
        item?.position?.lng
    );
    if (lat === null || lng === null) return null;

    return {lat, lng};
}

const joinValues = (...values) => values.filter(Boolean).join(" - ");

const formatDateValue = (value, format) => {
    if (!value) return null;

    const formattedValue = projectUtils.formatDate(value, format);

    return formattedValue && !String(formattedValue).includes("Invalid Date") ? formattedValue : value;
}

const formatDetailValue = (value) => {
    if (value === null || value === undefined || value === "") return null;

    if (Array.isArray(value)) {
        return value
            .map(item => {
                if (!item) return null;
                if (typeof item === "object") return item.name ?? item.description ?? item.id ?? null;

                return item;
            })
            .filter(Boolean)
            .join(", ");
    }

    if (typeof value === "object") return value.name ?? value.description ?? value.id ?? null;

    return value;
}

const detail = (label, value) => {
    const formattedValue = formatDetailValue(value);

    return formattedValue === null || formattedValue === undefined || formattedValue === ""
        ? null
        : {label, value: formattedValue};
}

const formatCpfCnpj = (item) => {
    if (item?.cpf) return projectUtils.formatCpfToView(String(item.cpf));
    if (item?.cnpj) return projectUtils.formatCnpjToView(String(item.cnpj));

    return null;
}

const getAddressSearchText = (address) => {
    const addressValue = projectUtils.addressToString(
        address?.city ?? address?.municipality,
        address?.neighborhood ?? address?.district,
        address?.address ?? address?.street,
        address?.number
    );

    return joinValues(addressValue, address?.zipCode ?? address?.zipcode ?? address?.cep);
}

const markerId = (prefix, item, index) => `${prefix}-${item?.itemId ?? item?.id ?? index}`;

const getBoConfig = (boName) => ({
    ...INDIVIDUAL_MAP_TYPES.bo,
    label: boName ?? INDIVIDUAL_MAP_TYPES.bo.label,
    singular: boName ?? INDIVIDUAL_MAP_TYPES.bo.singular
});

const buildAddressMarker = (address, index, position) => {
    const config = address.type === "COMERCIAL" ? INDIVIDUAL_MAP_TYPES.address_comercial : INDIVIDUAL_MAP_TYPES.address_residencial;
    const addressText = getAddressSearchText(address);

    return {
        id: markerId("address", address, index),
        type: "address",
        label: config.singular,
        listLabel: config.label,
        title: address?.type ? `${config.singular} - ${address.type}` : config.singular,
        description: addressText,
        date: address?.date,
        dateFormat: "DD/MM/YYYY",
        position,
        icon: config.icon,
        rawItem: address
    };
}

const getAssociationConfig = (type, boName) => {
    if (type === "bo") return getBoConfig(boName);

    return INDIVIDUAL_MAP_TYPES[type];
}

const buildAssociationMarker = (item, index, type, boName) => {
    const position = getPositionFromItem(item);
    if (!position) return null;

    const config = getAssociationConfig(type, boName);

    const titleMap = {
        encounter: config.singular,
        bo: item?.id ? `${config.singular} ${item.id}` : config.singular,
        occurrence: item?.id ? `${config.singular} ${item.id}` : config.singular,
        sos: item?.name ?? config.singular
    };

    return {
        id: markerId(type, item, index),
        type,
        label: config.singular,
        listLabel: config.label,
        title: titleMap[type] ?? config.singular,
        position,
        icon: config.icon,
        rawItem: item
    };
}

const buildIndividualMapData = (individual, individualInfo, boName) => {
    const addressMarkers = toArray(individual?.addresses)
        .map((address, index) => {
            const position = getPositionFromItem(address);
            return position ? buildAddressMarker(address, index, position) : null;
        })
        .filter(Boolean);

    const associationMarkers = [
        ...(toArray(individualInfo?.encounters).map((item, index) => buildAssociationMarker(item, index, "encounter", null))),
        ...(toArray(individualInfo?.bos).map((item, index) => buildAssociationMarker(item, index, "bo", boName))),
        ...(toArray(individualInfo?.occurrences).map((item, index) => buildAssociationMarker(item, index, "occurrence", null))),
        ...(toArray(individualInfo?.sos).map((item, index) => buildAssociationMarker(item, index, "sos", null))),
    ].filter(Boolean);

    return [...addressMarkers, ...associationMarkers];
}

const getBoundsLiteral = (markers) => {
    if (!markers.length) return null;

    return markers.reduce((bounds, marker) => {
        const {lat, lng} = marker.position;

        return {
            north: Math.max(bounds.north, lat),
            south: Math.min(bounds.south, lat),
            east: Math.max(bounds.east, lng),
            west: Math.min(bounds.west, lng)
        };
    }, {
        north: markers[0].position.lat,
        south: markers[0].position.lat,
        east: markers[0].position.lng,
        west: markers[0].position.lng
    });
}

const getMarkerDetails = (marker) => {
    const item = marker.rawItem ?? {};

    const detailsByType = {
        address: [
            detail("Tipo", item?.type),
            detail("Logradouro", item?.address ?? item?.street),
            detail("Número", item?.number),
            detail("Bairro", item?.neighborhood ?? item?.district),
            detail("Município", item?.city ?? item?.municipality),
            detail("Complemento", item?.complement),
            detail("CEP", item?.zipCode ?? item?.zipcode ?? item?.cep),
            detail("Data", formatDateValue(item?.date, "DD/MM/YYYY")),
        ],
        encounter: [
            detail("Abordado por", item?.userNameWar ?? item?.userName),
            detail("D/H inicial", formatDateValue(item?.dtStart)),
            detail("Observação", item?.observation),
        ],
        bo: [
            detail("Número", item?.id),
            detail("Status", item?.status),
            detail("Natureza(s)", item?.reason),
            detail("D/H do fato", formatDateValue(item?.dtfact)),
        ],
        occurrence: [
            detail("Classificação", item?.classification),
            detail("Naturezas", item?.reason),
            detail("D/H inicial do fato", formatDateValue(item?.dtIni)),
            detail("D/H final do fato", formatDateValue(item?.dtEnd)),
        ],
        sos: [
            detail("Nome", item?.name),
            detail("CPF/CNPJ", formatCpfCnpj(item)),
            detail("Programa de proteção", item?.protectionProgram),
        ]
    };

    return [
        ...(detailsByType[marker.type] ?? []),
    ].filter(Boolean);
}

const canOpenMarker = (marker) => marker.type !== "address" && !!marker.rawItem?.id;

const openMarkerItem = (marker, navigate) => {
    const item = marker.rawItem ?? {};

    if (!item.id) return;

    if (marker.type === "encounter") {
        navigate(`/encounter/form/${item.id}/edit`, true);
        return;
    }

    if (marker.type === "bo") {
        const id = String(item.id).replace("/", "-");
        navigate(`/bo/redirect/${id}`, true);
        return;
    }

    if (marker.type === "occurrence") {
        projectUtils.openOccurrence(item.id);
        return;
    }

    if (marker.type === "sos") {
        navigate(`/citizens/form/${item.id}`, true);
    }
}

const IndividualActivityInfoWindow = ({marker, onOpenItem}) => {
    const details = getMarkerDetails(marker);
    const showOpenLink = canOpenMarker(marker);

    return (
        <div className="min-w-64 max-w-80 text-sm text-gray-700">
            <div className="flex flex-col gap-1.5">
                {
                    details.length
                        ? details.map(({label, value}) => (
                            <div key={label} className="grid grid-cols-[112px_1fr] gap-2">
                                <span className="font-semibold text-gray-600">{label}:</span>
                                <span className="break-words text-gray-800">{value}</span>
                            </div>
                        ))
                        : <span className="text-gray-500">Sem informações.</span>
                }
            </div>

            {
                showOpenLink &&
                <button
                    type="button"
                    className="mt-3 w-full !cursor-pointer rounded bg-[#063A6C] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#052f57]"
                    style={{cursor: "pointer", pointerEvents: "auto"}}
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                        event.stopPropagation();
                        onOpenItem(marker);
                    }}
                >
                    Abrir {marker.label}
                </button>
            }
        </div>
    );
}

const IndividualActivityMarker = ({marker, isOpen, onOpen, onClose, onOpenItem}) => {
    const [markerRef, advancedMarker] = useAdvancedMarkerRef();

    return (
        <>
            <AdvancedMarker
                ref={markerRef}
                position={marker.position}
                title={marker.title}
                onClick={(event) => {
                    event?.domEvent?.stopPropagation?.();
                    onOpen();
                }}
            >
                <img
                    className="h-10 w-10 object-contain drop-shadow-md"
                    src={getAssetPath(`img/individual/${marker.icon}`)}
                    alt={marker.title}
                />
            </AdvancedMarker>
            {
                isOpen && advancedMarker &&
                <InfoWindow
                    anchor={advancedMarker}
                    maxWidth={360}
                    onCloseClick={onClose}
                >
                    <IndividualActivityInfoWindow
                        marker={marker}
                        onOpenItem={onOpenItem}
                    />
                </InfoWindow>
            }
        </>
    );
}

const IndividualActivityMapContent = ({markers, fallbackPosition}) => {
    const map = useMap();
    const navigate = navigateHook();
    const [activeMarkerId, setActiveMarkerId] = useState(null);

    useEffect(() => {
        if (!map) return;

        if (!markers.length) {
            map.setCenter(fallbackPosition);
            map.setZoom(13);
            return;
        }

        if (markers.length === 1) {
            map.setCenter(markers[0].position);
            map.setZoom(15);
            return;
        }

        map.fitBounds(getBoundsLiteral(markers), 60);
    }, [map, markers, fallbackPosition]);

    useEffect(() => {
        if (activeMarkerId && !markers.some(marker => marker.id === activeMarkerId)) {
            setActiveMarkerId(null);
        }
    }, [activeMarkerId, markers]);

    useEffect(() => {
        if (!map) return;

        const listener = map.addListener("click", () => setActiveMarkerId(null));

        return () => listener.remove();
    }, [map]);

    return (
        <>
            {
                markers.map(marker => (
                    <IndividualActivityMarker
                        key={`${marker.id}-${marker.position.lat}-${marker.position.lng}`}
                        marker={marker}
                        isOpen={activeMarkerId === marker.id}
                        onOpen={() => setActiveMarkerId(marker.id)}
                        onClose={() => setActiveMarkerId(null)}
                        onOpenItem={(selectedMarker) => openMarkerItem(selectedMarker, navigate)}
                    />
                ))
            }

            {
                !markers.length &&
                <MapControl position={ControlPosition.BOTTOM_CENTER}>
                    <div className="mb-4 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 shadow-md">
                        Nenhuma localização disponível.
                    </div>
                </MapControl>
            }
        </>
    );
}

export const IndividualActivityMap = ({individual, individualInfo, boName}) => {
    const {data: corporation, isLoading} = useQuery({
        queryFn: () => corporationService.entity(),
        queryKey: ["corporation"]
    });

    const fallbackPosition = useMemo(() => ({
        lat: normalizeCoordinate(corporation?.latitude) ?? 0,
        lng: normalizeCoordinate(corporation?.longitude) ?? 0
    }), [corporation]);

    const markers = useMemo(
        () => buildIndividualMapData(individual, individualInfo, boName),
        [individual, individualInfo, boName]
    );

    if (isLoading) {
        return (
            <div className="flex h-[calc(100vh-133px)] min-h-[420px] items-center justify-center text-sm text-gray-600">
                Carregando mapa...
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-133px)] min-h-[420px] w-full overflow-hidden rounded-md">
            <APIProvider apiKey={corporation?.maps_api_key ?? ""} language="pt-BR">
                <GoogleMap
                    mapId={DEFAULT_MAP_ID}
                    defaultZoom={13}
                    defaultCenter={fallbackPosition}
                    reuseMaps={true}
                    gestureHandling="greedy"
                    style={{height: "100%", width: "100%"}}
                    fullscreenControl={true}
                    mapTypeControl={true}
                    streetViewControl={false}
                    fullscreenControlOptions={{position: ControlPosition.BOTTOM_RIGHT}}
                    mapTypeControlOptions={{position: ControlPosition.TOP_RIGHT}}
                >
                    <IndividualActivityMapContent
                        markers={markers}
                        fallbackPosition={fallbackPosition}
                    />
                </GoogleMap>
            </APIProvider>
        </div>
    );
}

export default IndividualActivityMap;
